// TODO remove @colonist/i18n usage
import {type TranslationObject} from '@colonist/i18n'

export namespace TimeUtils {
    export const MILLISECONDS = 1000
    export const SECONDS_IN_MINUTE = 60
    export const SECONDS_IN_HOUR = 3600
    export const MINUTES_IN_HOUR = 60
    export const SECONDS_IN_DAY = 86400
    export const HOURS_IN_DAY = 24
    export const DAYS_IN_WEEK = 7
    export const DAYS_IN_MONTH = 30
    export const DAYS_IN_YEAR = 365.25
    export const WEEKS_IN_MONTH = DAYS_IN_MONTH / DAYS_IN_WEEK
    export const MONTHS_IN_YEAR = 12

    export enum TimeType {
        Day = 'day',
        Hour = 'hour',
        Minute = 'minute',
    }

    export function isDatePassed(stringDate: string): boolean {
        const date = new Date(stringDate)
        const now = new Date()
        if(now > date) return true
        return false
    }

    export function isDateInTheFuture(date: Date | undefined | null): boolean {
        if(date == undefined) return false
        if(new Date() < date) return true

        return false
    }

    export function millisecondsLeftInUTCDay(today: Date): number {
        const hoursLeftInCurrentDay = HOURS_IN_DAY - today.getUTCHours() - 1
        const minutesLeftInCurrentHour = MINUTES_IN_HOUR - today.getUTCMinutes() - 1
        const secondsLeftInCurrentMinute = SECONDS_IN_MINUTE - today.getUTCSeconds() - 1
        const millisecondsLeftInCurrentSecond = MILLISECONDS - today.getUTCMilliseconds() - 1

        const secondsLeftInDay = secondsLeftInCurrentMinute + minutesLeftInCurrentHour * SECONDS_IN_MINUTE + hoursLeftInCurrentDay * SECONDS_IN_HOUR
        const millisecondsLeftInDay = secondsLeftInDay * MILLISECONDS + millisecondsLeftInCurrentSecond

        return millisecondsLeftInDay
    }

    export function getDate(theDate: Date): string {
        const year = theDate.getFullYear()
        const month = String(theDate.getMonth() + 1).padStart(2, '0')
        const day = String(theDate.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    export function getDateTime(theDate: Date): string {
        const date = getDate(theDate)
        const hour = String(theDate.getHours()).padStart(2, '0')
        const minute = String(theDate.getMinutes()).padStart(2, '0')
        const second = String(theDate.getSeconds()).padStart(2, '0')

        return `${date}-${hour}-${minute}-${second}`
    }

    export function getHoursAgoInTimestampFormat(hour: number): number {
        const hoursAgo = hour * MINUTES_IN_HOUR * SECONDS_IN_MINUTE * MILLISECONDS
        return Math.floor((Date.now() - hoursAgo) / MILLISECONDS)
    }

    export function convertTimestampToDateTimeText(time: number): TranslationObject {
        const date = new Date(Number(time))
        const dateFormatParams = {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false}

        return {
            key: 'strings:colonistCommon.utils.#date',
            options: {
                value: date,
                formatParams: {
                    value: dateFormatParams,
                },
            },
        }
    }

    export function timeToString(time: number): string {
        const doubleDigitHourStartingPoint = 10
        const sliceLetterAmount = -2

        const totalSeconds = time / MILLISECONDS
        const hour = Math.floor(totalSeconds / (MINUTES_IN_HOUR * SECONDS_IN_MINUTE))
        const min = Math.floor(totalSeconds % SECONDS_IN_HOUR / SECONDS_IN_MINUTE)
        const sec = Math.floor(totalSeconds % SECONDS_IN_MINUTE)
        let newHour = ''
        if(hour < doubleDigitHourStartingPoint) {
            newHour = ('0' + hour).slice(sliceLetterAmount)
        } else {
            newHour = hour.toString()
        }
        return newHour + ':' + ('0' + min).slice(sliceLetterAmount) + ':' + ('0' + sec).slice(sliceLetterAmount)
    }

    export function timePadding(time: number): string {
        const doubleDigitHourStartingPoint = 10
        return time < doubleDigitHourStartingPoint ? '0' + time.toString() : time.toString()
    }

    export function getUTCTimeString(time: number): string {
        const dateTime = new Date(time)
        return dateTime.toUTCString()
    }

    // Estimation of minutes, not accurate under 5 minutes, use millisecondsPassedSince instead
    export function minutesPassedSince(time: number): number {
        const now = Date.now()
        const difference = now - time
        const totalSeconds = difference / MILLISECONDS
        return Math.round(totalSeconds / SECONDS_IN_MINUTE)
    }

    export function millisecondsPassedSince(time: number): number {
        const now = Date.now()
        return now - time
    }

    export function isThisLastMonth(time: number, from: number = Date.now()): boolean {
        const hour = MILLISECONDS * SECONDS_IN_MINUTE * MINUTES_IN_HOUR
        const day = hour * HOURS_IN_DAY
        const month = day * DAYS_IN_MONTH
        const aMonthAgo = from - month
        return time > aMonthAgo
    }

    export function isThisLastYear(time: number, from: number = Date.now()): boolean {
        const hour = MILLISECONDS * SECONDS_IN_MINUTE * MINUTES_IN_HOUR
        const day = hour * HOURS_IN_DAY
        const month = day * DAYS_IN_MONTH
        const year = month * MONTHS_IN_YEAR
        const aYearAgo = from - year
        return time > aYearAgo
    }

    export function secondsLeftForCurrentState(timeLeft: number): number {
        return timeLeft < 0 ? 0 : Math.round(timeLeft)
    }

    // https://stackoverflow.com/a/20863404/2589276
    export function yesterdayDate(): Date {
        return adjustDateByDays(-1)
    }

    // https://stackoverflow.com/a/6117889/2589276
    export function getWeekNumber(milliseconds: number): number[] {
        const date = new Date(milliseconds)
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        // noinspection TsLint Don't know what these values are
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7))
        // Get first day of year
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
        // Calculate full weeks to nearest Thursday
        // noinspection TsLint Don't know what these values are
        const weekNo: number = Math.ceil((((+date - +yearStart) / 86400000) + 1) / 7)
        // Return array of year and week number
        return [weekNo, date.getUTCFullYear()]
    }

    // https://stackoverflow.com/a/13904120/14006099
    export function getDaysHoursMinutesBetweenDates(futureDate: Date, pastDate: Date): {days: number, hours: number, minutes: number} {
        let deltaSeconds = (futureDate.getTime() - pastDate.getTime()) / MILLISECONDS

        const deltaDays = Math.floor(deltaSeconds / DurationConverter.daysAsSeconds(1))
        deltaSeconds -= DurationConverter.daysAsSeconds(deltaDays)

        const deltaHours = Math.floor(deltaSeconds / DurationConverter.hoursAsSeconds(1)) % HOURS_IN_DAY
        deltaSeconds -= DurationConverter.hoursAsSeconds(deltaHours)

        const deltaMinutes = Math.floor(deltaSeconds / DurationConverter.minutesAsSeconds(1)) % SECONDS_IN_MINUTE
        deltaSeconds -= DurationConverter.minutesAsSeconds(deltaMinutes)

        return {days: deltaDays, hours: deltaHours, minutes: deltaMinutes}
    }

    export function getDaysHoursMinutesBetweenDatesString(futureDate: Date, pastDate: Date): string {
        const deltaDaysHoursMinutes = getDaysHoursMinutesBetweenDates(futureDate, pastDate)
        return `${deltaDaysHoursMinutes.days} days ${deltaDaysHoursMinutes.hours} hours ${deltaDaysHoursMinutes.minutes} minutes`
    }

    export function getDaysHoursOrMinutesBetweenDates(futureDate: Date, pastDate: Date): {dateType: TimeType, duration: number} {
        const deltaDaysHoursMinutes = getDaysHoursMinutesBetweenDates(futureDate, pastDate)
        const {days, hours, minutes} = deltaDaysHoursMinutes
        if(days > 0) return {dateType: TimeType.Day, duration: days}
        if(hours > 0) return {dateType: TimeType.Hour, duration: hours}
        return {dateType: TimeType.Minute, duration: minutes}
    }

    export function getOneWeekInThePast(): Date {
        return adjustDateByDays(-TimeUtils.DAYS_IN_WEEK)
    }

    export function getOneWeekInTheFuture(): Date {
        return adjustDateByDays(TimeUtils.DAYS_IN_WEEK)
    }

    export function getNDaysInThePast(days: number): Date {
        return adjustDateByDays(-days)
    }

    export function adjustDateByDays(days: number, date: Date = new Date()): Date {
        return new Date(date.getTime() + (DurationConverter.daysAsMilliseconds(days)))
    }

    export function adjustDateByMinutes(minutes: number, date: Date = new Date()): Date {
        return new Date(date.getTime() + (DurationConverter.minutesAsMilliseconds(minutes)))
    }

    export function adjustDateByMilliseconds(milliseconds: number, date: Date = new Date()): Date {
        return new Date(date.getTime() + (milliseconds))
    }

    export function formatSecondsToElapsedString(seconds: number) {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds - (hours * 3600)) / 60)
        const remainingSeconds = seconds - (hours * 3600) - (minutes * 60)

        let formattedTime = minutes.toString().padStart(2, '0') + ':' +
                            remainingSeconds.toString().padStart(2, '0')

        if(hours) formattedTime = hours.toString().padStart(2, '0') + ':' + formattedTime

        return formattedTime
    }

    export function absoluteDifferenceInDays(startDate: Date, endDate: Date) {
        const difference = Math.abs(startDate.getTime() - endDate.getTime())
        return (difference / (TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY))
    }

    export function differenceInDays(startDate: Date, endDate: Date) {
        const difference = endDate.getTime() - startDate.getTime()
        return (difference / (TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY))
    }

    export function isDateInBetween(startDate: Date, endDate: Date, dateToCheck: Date): boolean {
        return startDate <= dateToCheck && dateToCheck <= endDate
    }

    export function addYears(date: Date, numberOfYears: number): Date {
        const result = new Date(date.getTime())
        result.setUTCFullYear(result.getUTCFullYear() + numberOfYears)
        return result
    }

    export function toDate(date: Date | string | number): Date {
        if(date instanceof Date) return date
        return new Date(date)
    }

    export function getStartOfDay(date: Date): Date {
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0))
    }

    export function getEndOfDay(date: Date): Date {
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999))
    }

    export function formatDateDistance(olderDate: Date, newerDate: Date): TranslationObject {
        const daysDiff = absoluteDifferenceInDays(olderDate, newerDate)

        if(daysDiff < 1) return {key: 'strings:colonistCommon.dates.day', options: {count: 1}}
        if(daysDiff < DAYS_IN_MONTH) return {key: 'strings:colonistCommon.dates.day', options: {count: Math.round(daysDiff)}}

        const monthsDiff = daysDiff / DAYS_IN_MONTH

        return {key: 'strings:colonistCommon.dates.month', options: {count: Math.round(monthsDiff)}}
    }

    export function formatCountdownString(date: Date): string {
        const now = new Date()
        const milliseconds = date.getTime() - now.getTime()
        const totalSeconds = milliseconds / MILLISECONDS

        const days = totalSeconds / SECONDS_IN_DAY
        if(days >= 1) return `${Math.floor(days)}d`

        const hours = totalSeconds / SECONDS_IN_HOUR
        if(hours >= 1) return `${Math.floor(hours)}h`

        const minutes = totalSeconds / SECONDS_IN_MINUTE
        if(minutes >= 1) return `${Math.floor(minutes)}m`

        const seconds = totalSeconds % SECONDS_IN_MINUTE
        if(seconds >= 1) return `${Math.floor(seconds)}s`

        return '0s'
    }

    export function formatTimeLeft(date: Date): TranslationObject {
        const now = new Date()
        const milliseconds = date.getTime() - now.getTime()
        const totalSeconds = milliseconds / MILLISECONDS

        const days = totalSeconds / SECONDS_IN_DAY
        if(days >= 1) return {key: 'strings:colonistCommon.timeLeft.days', options: {count: Math.floor(days)}}

        const hours = totalSeconds / SECONDS_IN_HOUR
        if(hours >= 1) return {key: 'strings:colonistCommon.timeLeft.hours', options: {count: Math.floor(hours)}}

        const minutes = totalSeconds / SECONDS_IN_MINUTE
        if(minutes >= 1) return {key: 'strings:colonistCommon.timeLeft.minutes', options: {count: Math.floor(minutes)}}

        const seconds = totalSeconds % SECONDS_IN_MINUTE
        if(seconds >= 1) return {key: 'strings:colonistCommon.timeLeft.seconds', options: {count: Math.floor(seconds)}}

        return {key: 'strings:colonistCommon.timeLeft.seconds', options: {count: 0}}
    }

    export function getAdaptiveCountdownPollIntervalMs(remainingMs: number): number {
        const dayMs = DurationConverter.daysAsMilliseconds(1)
        const hourMs = DurationConverter.hoursAsMilliseconds(1)
        const minuteMs = DurationConverter.minutesAsMilliseconds(1)
        const secondMs = DurationConverter.secondsAsMilliseconds(1)
        if(remainingMs >= dayMs) return hourMs
        if(remainingMs >= hourMs) return minuteMs
        if(remainingMs <= minuteMs) return secondMs
        return Math.min(minuteMs, remainingMs - minuteMs)
    }

    export function getTimeAgoTextFromDate(date: Date): TranslationObject {
        const now = new Date()

        const secondsAgo = Math.floor((now.getTime() - date.getTime()) / MILLISECONDS)
        if(secondsAgo < SECONDS_IN_MINUTE) return {key: 'strings:popups.notification.timeAbbreviations.second', options: {count: secondsAgo}}

        const minutesAgo = Math.floor(secondsAgo / SECONDS_IN_MINUTE)
        if(minutesAgo < MINUTES_IN_HOUR) return {key: 'strings:popups.notification.timeAbbreviations.minute', options: {count: minutesAgo}}

        const hoursAgo = Math.floor(minutesAgo / MINUTES_IN_HOUR)
        if(hoursAgo < HOURS_IN_DAY) return {key: 'strings:popups.notification.timeAbbreviations.hour', options: {count: hoursAgo}}

        const daysAgo = Math.floor(hoursAgo / HOURS_IN_DAY)
        if(daysAgo < DAYS_IN_WEEK) return {key: 'strings:popups.notification.timeAbbreviations.day', options: {count: daysAgo}}

        const weeksAgo = Math.floor(daysAgo / DAYS_IN_WEEK)
        if(weeksAgo < WEEKS_IN_MONTH) return {key: 'strings:popups.notification.timeAbbreviations.week', options: {count: weeksAgo}}

        const monthsAgo = Math.floor(daysAgo / DAYS_IN_MONTH)
        if(monthsAgo < MONTHS_IN_YEAR) return {key: 'strings:popups.notification.timeAbbreviations.month', options: {count: monthsAgo}}

        const yearsAgo = Math.floor(daysAgo / DAYS_IN_YEAR)
        return {key: 'strings:popups.notification.timeAbbreviations.year', options: {count: yearsAgo}}
    }
}

export namespace DurationConverter {
    export function secondsAsMilliseconds(seconds: number) {
        return seconds * TimeUtils.MILLISECONDS
    }

    export function minutesAsMilliseconds(minutes: number) {
        return minutes * secondsAsMilliseconds(TimeUtils.SECONDS_IN_MINUTE)
    }

    export function hoursAsMilliseconds(hours: number) {
        return hours * minutesAsMilliseconds(TimeUtils.MINUTES_IN_HOUR)
    }

    export function daysAsMilliseconds(days: number) {
        return days * hoursAsMilliseconds(TimeUtils.HOURS_IN_DAY)
    }

    export function weeksAsMilliseconds(weeks: number) {
        return weeks * daysAsMilliseconds(TimeUtils.DAYS_IN_WEEK)
    }

    export function monthsAsMilliseconds(months: number) {
        return months * daysAsMilliseconds(TimeUtils.DAYS_IN_MONTH)
    }

    export function minutesAsSeconds(minutes: number) {
        return minutes * TimeUtils.SECONDS_IN_MINUTE
    }

    export function hoursAsSeconds(hours: number) {
        return hours * minutesAsSeconds(TimeUtils.MINUTES_IN_HOUR)
    }

    export function daysAsSeconds(days: number) {
        return days * hoursAsSeconds(TimeUtils.HOURS_IN_DAY)
    }

    export function weeksAsSeconds(weeks: number) {
        return weeks * daysAsSeconds(TimeUtils.DAYS_IN_WEEK)
    }

    export function monthsAsSeconds(months: number) {
        return months * daysAsSeconds(TimeUtils.DAYS_IN_MONTH)
    }
}
